import { state } from '../state.js';
import { FudoGpRepository } from '../infrastructure/fudo-gp.repository.js';
import { CONFIG } from '../config.js';
import { formatPrice } from '../utils/formatters.js';

/**
 * La opción de leche vegetal se identifica por el NOMBRE del producto que Fudo
 * usa como opción del grupo ("leche vegetal"), no por un id fijo: si el staff
 * recrea la opción en Fudo el id cambia, el nombre no.
 */
const VEGETAL_MILK_RE = /leche\s+vegetal/i;

/**
 * La aclaración es SOLO de cafetería: aplica al grupo cuya elección ES el tipo
 * de leche ("leche comun o vegetal", categorías INFUSIONES CLASICAS/ESPECIALES).
 *
 * Fudo tiene la opción cargada también en el grupo "agua o leche" (licuados,
 * té, mate cocido), pero ahí el local NO la ofrece — decisión del owner,
 * 2026-08-26. Discriminar por nombre de grupo (no por id) evita hardcodear un
 * id que cambia si el grupo se recrea; si el staff renombra el grupo en Fudo,
 * la nota desaparece y hay que actualizar este patrón.
 */
const MILK_CHOICE_GROUP_RE = /leche.*vegetal|vegetal.*leche/i;

/**
 * Servicio de catálogo. Ambas vistas leen de la API GENERAL PURPOSE (GP) de
 * Fudo (migración 2026-07: la carta pasó de la API de integraciones a GP para
 * poder ordenar los productos por `position`, el orden que el staff define en
 * Fudo — la API de integraciones NO exponía ese campo).
 *  - Carta pública (`init`): catálogo GP normalizado al shape histórico de la
 *    carta (ids numéricos + `productCategoryId`), ordenado por `position`.
 */
export const MenuService = {
    /** Carta pública — catálogo GP normalizado y ordenado por `position`. */
    async init() {
        state.loading = true;
        try {
            const [gpCategories, gpCatalog, gpModifierOptions] = await Promise.all([
                FudoGpRepository.getCategories(),
                FudoGpRepository.getCatalog(),
                // Las aclaraciones de modificador son un EXTRA de la carta: si
                // este endpoint falla la carta se muestra igual, sin la nota.
                FudoGpRepository.getModifierOptions().catch(error => {
                    window.Sentry?.captureException(error);
                    return [];
                })
            ]);
            const gpProducts = gpCatalog.products;

            // La GP usa JSON:API: ids string y campo `categoryId`. `_processData`
            // y `CONFIG.SECCIONES_CARTA` fueron escritos para el shape de la API
            // de integraciones: ids NUMÉRICOS y `productCategoryId`. El id-space
            // de categorías coincide 1:1 entre ambas APIs (verificado contra la
            // API en vivo), así que sólo normalizamos tipo y nombre de campo, y
            // preservamos `position` para que menu.js ordene como en Fudo.
            const categories = gpCategories.map(c => ({ ...c, id: Number(c.id) }));
            const products = gpProducts.map(p => ({
                ...p,
                productCategoryId: p.categoryId != null ? Number(p.categoryId) : null,
            }));

            this._processData(categories, products);
            // Se construye con el catálogo COMPLETO (no `state.products`): el
            // producto que Fudo usa como opción ("leche vegetal") vive en una
            // categoría de servicio que la carta podría llegar a excluir.
            state.categoryNotes = this._buildCategoryNotes(
                products,
                gpModifierOptions,
                gpCatalog.modifierGroups,
            );
            state.loading = false;
            return true;
        } catch (error) {
            window.Sentry?.captureException(error);
            state.error = "Error al cargar el menú. Mostrando datos de respaldo.";
            state.loading = false;
            return false;
        }
    },

    /**
     * Aclaraciones por categoría derivadas de los GRUPOS DE MODIFICADORES de Fudo.
     *
     * Hoy resuelve una sola: la leche vegetal de la cafetería. Nada se
     * hardcodea — el precio sale de `attributes.price` de la opción en Fudo y
     * las categorías salen de qué productos ofrecen el grupo de elección de
     * leche (hoy INFUSIONES CLASICAS e INFUSIONES ESPECIALES). Si el local
     * cambia el precio en Fudo, la carta lo refleja sin tocar código.
     *
     * @param {Array<{id: string|number, name: string, productCategoryId: number|null, modifierGroupIds?: string[]}>} allProducts
     * @param {Array<{price: number, productId: string|null, groupId: string|null}>} modifierOptions
     * @param {Array<{id: string, name: string}>} modifierGroups
     * @returns {Map<number, string>} categoryId → texto de la nota
     */
    _buildCategoryNotes(allProducts, modifierOptions, modifierGroups) {
        const notes = new Map();
        if (!Array.isArray(modifierOptions) || modifierOptions.length === 0) return notes;

        const productById = new Map(allProducts.map(p => [String(p.id), p]));
        const milkChoiceGroupIds = new Set(
            (modifierGroups ?? [])
                .filter(g => MILK_CHOICE_GROUP_RE.test(g?.name ?? ''))
                .map(g => String(g.id)),
        );
        if (milkChoiceGroupIds.size === 0) return notes;

        // La opción sin costo ("leche comun") también matchearía un regex más
        // laxo; exigir price > 0 deja solo la que amerita aclaración.
        const priceByGroup = new Map();
        for (const opt of modifierOptions) {
            if (!(opt.price > 0) || !opt.groupId || !opt.productId) continue;
            if (!milkChoiceGroupIds.has(String(opt.groupId))) continue;
            if (!VEGETAL_MILK_RE.test(productById.get(opt.productId)?.name ?? '')) continue;
            const current = priceByGroup.get(opt.groupId);
            if (current == null || opt.price < current) priceByGroup.set(opt.groupId, opt.price);
        }
        if (priceByGroup.size === 0) return notes;

        // Si una categoría cae bajo dos grupos con precios distintos se anuncia
        // el MENOR: es el "desde" que el cliente puede esperar pagar.
        const priceByCategory = new Map();
        for (const product of allProducts) {
            if (product.productCategoryId == null) continue;
            for (const groupId of product.modifierGroupIds ?? []) {
                const price = priceByGroup.get(groupId);
                if (price == null) continue;
                const catId = Number(product.productCategoryId);
                const current = priceByCategory.get(catId);
                if (current == null || price < current) priceByCategory.set(catId, price);
            }
        }

        for (const [catId, price] of priceByCategory) {
            notes.set(catId, `Opción leche vegetal +${formatPrice(price)}`);
        }
        return notes;
    },

    /** Procesa y mapea los datos de la carta (integraciones) al estado. */
    _processData(allCategories, allProducts) {
        const excludedIds = new Set(
            allCategories
                .filter(c => CONFIG.FUDO.EXCLUDED_CATEGORIES.includes(c.name))
                .map(c => c.id)
        );

        state.products = allProducts.filter(p =>
            !excludedIds.has(p.productCategoryId)
        );

        const activeProdCatIds = new Set(state.products.map(p => p.productCategoryId));
        state.categories = allCategories.filter(cat => activeProdCatIds.has(cat.id));

        state.productsByCategory = new Map();
        for (const p of state.products) {
            if (!state.productsByCategory.has(p.productCategoryId)) {
                state.productsByCategory.set(p.productCategoryId, []);
            }
            state.productsByCategory.get(p.productCategoryId).push(p);
        }

        state.productsBySection = new Map();
        for (const section of CONFIG.SECCIONES_CARTA) {
            const ids = new Set(section.categoryIds.map(Number));
            state.productsBySection.set(section.id, state.products.filter(p => ids.has(Number(p.productCategoryId))));
        }
    }
};
